import { posthog } from '@/lib/posthog';
import { createClient } from '@/lib/supabase/client';

import { organizationManager } from './organizationManager';
import { regionManager } from './regionManager';

export interface AuthHeaderOptions {
  includeContentType?: boolean;
  organizationId?: string;
  /** Pre-resolved token; skips the session lookup. */
  accessToken?: string;
}

export async function getAuthHeaders(
  options: AuthHeaderOptions = {}
): Promise<Record<string, string>> {
  const { includeContentType = true, organizationId, accessToken } = options;

  let token = accessToken;
  if (!token) {
    if (typeof window === 'undefined') {
      throw new Error(
        'Access token is required for server-side usage. Use getServerAuthHeaders instead.'
      );
    }
    const {
      data: { session },
    } = await createClient().auth.getSession();
    token = session?.access_token;
  }

  if (!token) {
    throw new Error('User not authenticated');
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  const browserOrgId =
    typeof window === 'undefined'
      ? null
      : organizationManager.getOrganizationId();

  const orgId = organizationId || browserOrgId;
  if (orgId) {
    headers['x-organization-id'] = orgId;
  }

  const regionIds =
    orgId && orgId === browserOrgId ? regionManager.getRegionIds() : [];
  if (regionIds.length > 0) {
    headers['x-region-id'] = regionIds.join(',');
  }

  const sessionId = posthog.get_session_id();
  if (sessionId) {
    headers['x-posthog-session-id'] = sessionId;
  }

  return headers;
}

export interface ServerAuthHeaderOptions {
  accessToken: string;
  organizationId: string;
  regionIds: string[];
  includeContentType?: boolean;
}

export function getServerAuthHeaders(
  options: ServerAuthHeaderOptions
): Record<string, string> {
  const {
    accessToken,
    organizationId,
    regionIds,
    includeContentType = true,
  } = options;

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    'x-organization-id': organizationId,
  };

  if (regionIds.length > 0) {
    headers['x-region-id'] = regionIds.join(',');
  }

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
}
